import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Clock, Users, Calendar, ChevronRight, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getTimeEntries, getCustomers, getReportSummary } from "@/services/api";
import { format, parseISO, isToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { de } from "date-fns/locale";

export default function Dashboard() {
  const [recentEntries, setRecentEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    todayMinutes: 0,
    weekMinutes: 0,
    monthMinutes: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entriesData, customersData] = await Promise.all([
        getTimeEntries({ limit: 5 }),
        getCustomers(),
      ]);

      setRecentEntries(entriesData);
      setCustomers(customersData);

      // Calculate stats
      const today = format(new Date(), "yyyy-MM-dd");
      const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
      const weekEnd = format(endOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
      const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");
      const monthEnd = format(endOfMonth(new Date()), "yyyy-MM-dd");

      const [todayEntries, weekEntries, monthEntries] = await Promise.all([
        getTimeEntries({ start_date: today, end_date: today, limit: 100 }),
        getTimeEntries({ start_date: weekStart, end_date: weekEnd, limit: 100 }),
        getTimeEntries({ start_date: monthStart, end_date: monthEnd, limit: 100 }),
      ]);

      setStats({
        todayMinutes: todayEntries.reduce((sum, e) => sum + e.duration_minutes, 0),
        weekMinutes: weekEntries.reduce((sum, e) => sum + e.duration_minutes, 0),
        monthMinutes: monthEntries.reduce((sum, e) => sum + e.duration_minutes, 0),
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getCustomerById = (id) => customers.find((c) => c.id === id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-pulse text-zinc-500">Laden...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {format(new Date(), "EEEE, d. MMMM", { locale: de })}
          </p>
        </div>
        <Link to="/time-entry">
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-xl h-10 px-4 glow-amber"
            data-testid="new-entry-button"
          >
            <Plus size={18} className="mr-1" />
            Neu
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3" data-testid="stats-grid">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-amber-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Heute</span>
          </div>
          <p className="text-xl font-bold time-display" data-testid="stat-today">
            {formatDuration(stats.todayMinutes)}
          </p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={16} className="text-emerald-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Woche</span>
          </div>
          <p className="text-xl font-bold time-display" data-testid="stat-week">
            {formatDuration(stats.weekMinutes)}
          </p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-blue-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Monat</span>
          </div>
          <p className="text-xl font-bold time-display" data-testid="stat-month">
            {formatDuration(stats.monthMinutes)}
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="flex gap-3">
        <Link to="/clients" className="flex-1">
          <Card className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Users size={20} className="text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="customer-count">{customers.length}</p>
                <p className="text-xs text-zinc-500">Kunden</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/reports" className="flex-1">
          <Card className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp size={20} className="text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-hours">
                  {Math.round(stats.monthMinutes / 60)}h
                </p>
                <p className="text-xs text-zinc-500">Diesen Monat</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Entries */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-zinc-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Letzte Einträge
          </h2>
          <Link to="/reports" className="text-sm text-amber-500 hover:text-amber-400 flex items-center gap-1">
            Alle anzeigen
            <ChevronRight size={16} />
          </Link>
        </div>

        {recentEntries.length === 0 ? (
          <div className="empty-state" data-testid="empty-entries">
            <Clock size={48} className="text-zinc-700 mb-4" />
            <p className="text-zinc-500 mb-2">Noch keine Zeiteinträge</p>
            <Link to="/time-entry">
              <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800">
                Ersten Eintrag erstellen
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="recent-entries">
            {recentEntries.map((entry) => {
              const customer = getCustomerById(entry.customer_id);
              return (
                <Link
                  key={entry.id}
                  to={`/time-entry/${entry.id}`}
                  className="list-item group"
                  data-testid={`entry-${entry.id}`}
                >
                  <div
                    className="customer-dot mr-3"
                    style={{ backgroundColor: customer?.color || "#F59E0B" }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-zinc-100 truncate">
                      {customer?.name || "Unbekannt"}
                    </p>
                    <p className="text-sm text-zinc-500 truncate">
                      {entry.description || "Keine Beschreibung"}
                    </p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="font-semibold text-zinc-100 time-display">
                      {formatDuration(entry.duration_minutes)}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {isToday(parseISO(entry.date))
                        ? "Heute"
                        : format(parseISO(entry.date), "d. MMM", { locale: de })}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-zinc-600 ml-2 group-hover:text-zinc-400 transition-colors" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
